import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

/**
 * The file behind a document markup, opened once and shared by the editor and
 * the annotator. PDFs and images (PNG/JPG) expose the same interface:
 *
 *   numPages
 *   getPageSize(pageNumber)  -> { width, height } in page space (scale 1)
 *   renderPage(pageNumber, canvas, scale) -> { promise, cancel }
 *   destroy()
 *
 * renderPage draws onto a canvas the caller has already sized; `promise`
 * resolves to false when the render was cancelled.
 */

export const isPdfType = (fileType) => String(fileType || '').toLowerCase() === 'pdf';

export const base64ToBytes = (base64) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

const loadPdfSource = async (bytes) => {
    let pdf;
    try {
        // pdf.js hands the buffer to its worker, so give it a copy and keep
        // the original bytes usable for the export.
        pdf = await pdfjsLib.getDocument({ data: bytes.slice() }).promise;
    } catch (err) {
        if (err?.name === 'PasswordException') {
            throw new Error('This PDF is password-protected, so it cannot be opened for markup.');
        }
        throw new Error('This PDF could not be opened.');
    }

    return {
        kind: 'pdf',
        numPages: pdf.numPages,
        async getPageSize(pageNumber) {
            const page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1 });
            return { width: viewport.width, height: viewport.height };
        },
        renderPage(pageNumber, canvas, scale) {
            let task = null;
            let cancelled = false;
            const promise = (async () => {
                const page = await pdf.getPage(pageNumber);
                if (cancelled) return false;
                const viewport = page.getViewport({ scale });
                task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
                try {
                    await task.promise;
                } catch (err) {
                    if (err?.name === 'RenderingCancelledException') return false;
                    throw err;
                }
                return true;
            })();
            return {
                promise,
                cancel() {
                    cancelled = true;
                    if (task) task.cancel();
                },
            };
        },
        destroy() {
            pdf.destroy();
        },
    };
};

const loadImageSource = async (bytes) => {
    const url = URL.createObjectURL(new Blob([bytes]));
    let image;
    try {
        image = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('This image could not be opened.'));
            img.src = url;
        });
    } catch (err) {
        URL.revokeObjectURL(url);
        throw err;
    }

    // The natural size is the "page": it already accounts for the EXIF
    // orientation of phone photos.
    const size = { width: image.naturalWidth, height: image.naturalHeight };

    return {
        kind: 'image',
        numPages: 1,
        async getPageSize() {
            return size;
        },
        renderPage(pageNumber, canvas, scale) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
            return { promise: Promise.resolve(true), cancel() {} };
        },
        destroy() {
            URL.revokeObjectURL(url);
        },
    };
};

export const loadMarkupSource = (bytes, fileType) =>
    isPdfType(fileType) ? loadPdfSource(bytes) : loadImageSource(bytes);
